import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../utils/api';

const ConfigurationWrapper = ({ children }) => {
  const COMPLETED_KEY = 'company_setup_completed';
  const getInitialProgress = () => {
    // Si déjà marqué terminé, ne plus vérifier / ne plus afficher
    if (localStorage.getItem(COMPLETED_KEY) === 'true') {
      return 100;
    }
    // Fallback sur l'état existant
    const stepsState = localStorage.getItem('config_steps_state');
    if (stepsState) {
      try {
        const state = JSON.parse(stepsState);
        if (state.progress === 100) {
          localStorage.setItem(COMPLETED_KEY, 'true');
          return 100;
        }
        if (typeof state.progress === 'number') {
          return state.progress;
        }
      } catch {
        // ignore
      }
    }
    return 0;
  };

  const [configProgress, setConfigProgress] = useState(getInitialProgress);
  const [loading, setLoading] = useState(localStorage.getItem(COMPLETED_KEY) !== 'true');
  const navigate = useNavigate();

  // Charger le pourcentage de configuration
  const loadConfigurationProgress = async () => {
    // Une fois terminé, on ne vérifie plus du tout
    if (localStorage.getItem(COMPLETED_KEY) === 'true') {
      setConfigProgress(100);
      setLoading(false);
      return;
    }
    try {
      const response = await apiGet('/api/setup/settings');
      if (response.success && response.settings) {
        calculateConfigProgress(response.settings);
      } else {
        // Aucune configuration existante, progression à 0%
        setConfigProgress(0);
        // Nettoyer le localStorage des étapes
        localStorage.removeItem('config_steps_state');
        localStorage.removeItem(COMPLETED_KEY);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration:', error);
      // En cas d'erreur, on garde la progression du localStorage si elle existe
      // Sinon, on met à 0
      const stepsState = localStorage.getItem('config_steps_state');
      if (!stepsState) {
        setConfigProgress(0);
        localStorage.removeItem('config_steps_state');
        localStorage.removeItem(COMPLETED_KEY);
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculer le pourcentage de configuration selon les étapes
  const calculateConfigProgress = (settings) => {
    // D'abord, vérifier s'il y a un état validé dans localStorage
    const stepsState = localStorage.getItem('config_steps_state');
    if (stepsState) {
      try {
        const state = JSON.parse(stepsState);
        if (state.progress !== undefined) {
setConfigProgress(state.progress);
          return;
        }
      } catch (error) {
        console.error('Erreur lors de la lecture de l\'état des étapes:', error);
      }
    }
    
    // Si pas de localStorage, calculer basé sur les données du serveur
    if (!settings) {
      setConfigProgress(0);
      // Nettoyer le localStorage des étapes
      localStorage.removeItem('config_steps_state');
      return;
    }
    
let progress = 0;
    
    // Vérifier si multi-sites est activé
    const isMultiSites = settings.multi_sites || false;
    
    // Étape 1 (33%) : Configuration entreprise (nom de l'entreprise + ville + pays + email + adresse)
    const step1Complete = (settings.nom_entreprise || settings.nom) && settings.ville && settings.pays && settings.email_contact && settings.adresse;
    if (step1Complete) progress += 33;
    
    // Étape 2 (33%) : Localisation de l'entreprise
    const step2Complete = settings.latitude && settings.longitude;
    if (step2Complete) progress += 33;
    
    // Étape 3 (34%) : Horaires
    const step3Complete = settings.heure_ouverture && settings.heure_fermeture;
    if (step3Complete) progress += 34;
    
    setConfigProgress(Math.min(100, progress));
    
    localStorage.setItem('config_steps_state', JSON.stringify({
      step1: step1Complete,
      step2: step2Complete,
      step3: step3Complete,
      multiSites: isMultiSites,
      progress: Math.min(100, progress)
    }));

    if (Math.min(100, progress) === 100) {
      localStorage.setItem(COMPLETED_KEY, 'true');
    }
  };

  // Charger les données au montage
  useEffect(() => {
    // Si déjà terminé, ne rien faire (pas de vérification serveur, pas de flash)
    if (localStorage.getItem(COMPLETED_KEY) === 'true') {
      setConfigProgress(100);
      setLoading(false);
      return;
    }

    // Sinon charger depuis le serveur
    loadConfigurationProgress();
    
    // Écouter les mises à jour de progression depuis Configuration
    const handleConfigProgressUpdate = (event) => {
// Si la progression est fournie dans l'événement, l'utiliser directement
      if (event.detail && event.detail.progress !== undefined) {
        setConfigProgress(event.detail.progress);
// Si la progression est à 100%, mettre à jour aussi le localStorage
        if (event.detail.progress === 100) {
          localStorage.setItem(COMPLETED_KEY, 'true');
          const currentState = localStorage.getItem('config_steps_state');
          const state = currentState ? JSON.parse(currentState) : {};
          localStorage.setItem('config_steps_state', JSON.stringify({
            step1: true,
            step2: true,
            step3: true,
            multiSites: state.multiSites || false,
            progress: 100
          }));
        }
      } else {
        // Sinon, recharger depuis le serveur
        loadConfigurationProgress();
      }
    };
    
    window.addEventListener('configProgressUpdated', handleConfigProgressUpdate);
    
    // Nettoyage
    return () => {
      window.removeEventListener('configProgressUpdated', handleConfigProgressUpdate);
    };
  }, []);

  // Effet pour forcer un re-render quand la progression atteint 100%
  useEffect(() => {
    if (configProgress === 100) {
}
  }, [configProgress]);

  // Fonction pour rediriger vers la configuration avec l'étape appropriée
  const handleOpenConfig = () => {
    // Récupérer l'état des étapes depuis localStorage
    const stepsState = localStorage.getItem('config_steps_state');
    let targetStep = 1; // Par défaut, étape 1
    
    if (stepsState) {
      try {
        const state = JSON.parse(stepsState);
        if (!state.step1) targetStep = 1;
        else if (!state.step2) targetStep = 2;
        else if (!state.step3) targetStep = 3;
        else targetStep = 1;
      } catch (_) {}
    }
    
    // Sauvegarder l'étape cible dans localStorage pour la page Configuration
    localStorage.setItem('config_target_step', targetStep.toString());
    
    navigate('/configuration');
  };

  // Ne pas bloquer le contenu pendant le chargement - afficher directement
  // if (loading) {
  //   return (
  //     <div className="flex justify-center items-center h-64">
  //       <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  //       <span className="ml-3 text-gray-600">Chargement...</span>
  //     </div>
  //   );
  // }

  // Configuration terminée : rendre les enfants sans wrapper (après tous les hooks)
  if (localStorage.getItem(COMPLETED_KEY) === 'true' || configProgress === 100) {
    return children;
  }

  return (
    <div className="relative min-h-screen bg-white">
      {/* Bannière de configuration avec pourcentage - Uniquement si configuration incomplète */}
      {configProgress < 100 && (
        <div className="p-6">
          <div className="bg-[#0026E2] text-white px-6 py-4 rounded-lg mb-6 flex items-center justify-between shadow-lg max-w-6xl mx-auto">
            <div className="flex items-center">
              <FaExclamationTriangle className="text-xl mr-3" />
              <div>
                <h3 className="font-bold text-sm">Configuration requise</h3>
                <p className="text-xs">Veuillez configurer votre entreprise et sa localisation.</p>
                <div className="mt-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs">Progression: {configProgress}%</span>
                    <div className="w-32 bg-white/20 rounded-full h-2">
                      <div 
                        className="bg-white rounded-full h-2 transition-all duration-300"
                        style={{ width: `${configProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleOpenConfig}
                className="bg-white text-[#0026E2] px-4 py-1.5 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm whitespace-nowrap"
              >
                Configurer ({configProgress}%)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal avec effet de flou UNIQUEMENT si configuration incomplète */}
      {/* Le sidebar n'est pas flouté car il est en dehors de ce wrapper */}
      <div className={`${configProgress < 100 ? 'filter blur-sm opacity-30 pointer-events-none relative z-0' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default ConfigurationWrapper;
